import { useEffect, useRef } from "react";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  Timeline,
} from "@chakra-ui/react";
import { LuArrowLeft } from "react-icons/lu";
import { statusPresentation } from "../components/activityStatus";
import { PageHeader } from "../components/PageHeader";
import { useEffectivePersonId } from "../impersonation";
import { ActivityTaskCard } from "./ActivityTaskCard";
import {
  ActivityRefs,
  TagBadge,
  formatDay,
  localToday,
  stepWaitsOnViewer,
  type AssignmentDetail,
} from "./ActivityDetailParts";
import { ActivityHistory } from "./ActivityHistory";

/**
 * Whose activity this is, in the header, for anybody but them.
 *
 * A reviewer opening an activity from the queue is judging one particular
 * person's work, and the name used to sit in a small tile beside the dates.
 */
function AssigneeBlock({ subject }: { subject: NonNullable<AssignmentDetail["subject"]> }) {
  const name = <Text fontWeight="semibold" lineHeight="1.2">{subject.fullName}</Text>;
  return (
    <HStack gap={3}>
      <Avatar.Root size="lg">
        <Avatar.Fallback name={subject.fullName} />
        {subject.profileImageUrl ? (
          <Avatar.Image src={subject.profileImageUrl} alt={`${subject.fullName} profile`} />
        ) : null}
      </Avatar.Root>
      <Stack gap={0.5} minW={0}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
          Assigned to
        </Text>
        {subject.shortName ? (
          <Link to="/cemmies/$shortName" params={{ shortName: subject.shortName }}>
            <Box _hover={{ textDecoration: "underline" }}>{name}</Box>
          </Link>
        ) : name}
        {subject.groupName ? <Text fontSize="sm" color="fg.muted">{subject.groupName}</Text> : null}
      </Stack>
    </HStack>
  );
}

/**
 * What the activity is at, in one card: its state, what is expected and when,
 * and what it is filed under.
 *
 * This was three cards -- the state with two boxed dates, a Dependencies card
 * and a Revision notes card -- and the last two usually said that there was
 * nothing to say. A prerequisite is said where it matters, in the note below;
 * a revision note is said in the step it was given on, where it is acted on.
 */
function ActivitySummary({ detail, todayDay }: { detail: AssignmentDetail; todayDay: string }) {
  // Somebody else's activity, waiting on the viewer: the co-supervisor whose
  // step is next, the supervisor who signs. "Waiting on someone else" was
  // true of the activity and wrong for the one person reading it. On their
  // own activity the stored status already speaks to them.
  const isOwn = detail.subject === null || detail.subject.isViewer;
  const waitingOnYou = !isOwn && detail.tasks.some(stepWaitsOnViewer);
  const overdue = detail.dueDay !== null && detail.dueDay < todayDay && detail.status !== "completed";
  const look = statusPresentation(waitingOnYou ? "waiting_on_you" : detail.status, overdue);
  // Only a prerequisite makes "blocked" mean "waiting on another activity";
  // otherwise it is waiting for its opening day. Cancelled and archived are
  // not in the colour scheme and keep the server's word.
  const keepsServerLabel = !waitingOnYou && !overdue && (
    (detail.status === "blocked" && detail.blockedBy.length === 0)
    || detail.status === "cancelled"
    || detail.status === "archived"
  );

  const facts = [
    detail.dueDay === null ? null : `Due ${formatDay(detail.dueDay)}`,
    detail.opensOnDay !== null && detail.opensOnDay > todayDay ? `Opens ${formatDay(detail.opensOnDay)}` : null,
    detail.recommendedStartDay === null ? null : `Start from ${formatDay(detail.recommendedStartDay)}`,
  ].filter((fact) => fact !== null);

  return (
    <Card.Root as="section" borderWidth="1px" borderColor="border">
      <Card.Body gap={4} p={6}>
        <HStack flexWrap="wrap" gap={3}>
          <Badge colorPalette={look.tone} variant="subtle" size="lg" gap={1.5}>
            <Icon as={look.icon} boxSize={3.5} />
            {keepsServerLabel ? detail.statusLabel : look.label}
          </Badge>
          {/* Always said, in words: required is the usual case, and still
              worth reading. */}
          <Text fontSize="sm" color="fg.muted">
            <Text as="span" fontWeight="semibold">{detail.required ? "Required" : "Encouraged"}</Text>
            {facts.map((fact) => ` · ${fact}`).join("")}
          </Text>
        </HStack>

        {detail.tagSnapshots.length > 0 ? (
          <HStack flexWrap="wrap" gap={2}>
            {detail.tagSnapshots.map((tag) => (
              <TagBadge key={tag.tagId} name={tag.name} color={tag.color} title={tag.key} />
            ))}
          </HStack>
        ) : null}

        {/* A dependency is an order of COMPLETION, not permission to begin:
            the steps below stay open. Said plainly here, because an activity
            whose every step is approved and which is still not complete
            otherwise reads as broken. */}
        {detail.status === "blocked" && detail.blockedBy.length > 0 ? (
          <Box borderWidth="1px" borderColor="orange.muted" bg="orange.subtle" px={4} py={3}>
            <Text fontSize="sm">
              {detail.allStepsDone
                ? "Every step here is done. This cannot be completed until "
                : "This is completed after "}
              <ActivityRefs items={detail.blockedBy} />
              {detail.allStepsDone
                ? " is."
                : ". You can still work on the steps below in the meantime."}
            </Text>
          </Box>
        ) : null}
      </Card.Body>
    </Card.Root>
  );
}

// Where the trail and the back button lead, by where the page was opened from.
const ORIGINS = {
  reviews: { label: "Reviews", to: "/activities/reviews" },
  dashboard: { label: "Dashboard", to: "/" },
  activities: { label: "Activities", to: "/activities" },
} as const;

export function ActivityDetailPage() {
  const todayDay = localToday();

  const params = useParams({ from: "/_authed/activities/$personActivityId", structuralSharing: true });
  const { from } = useSearch({ from: "/_authed/activities/$personActivityId" });
  const origin = ORIGINS[from ?? "activities"];
  const effectivePersonId = useEffectivePersonId();
  const detail = useQuery(api.personActivities.getPersonActivityDetail, {
    personActivityId: params.personActivityId as Id<"personActivities">,
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  }) as AssignmentDetail | undefined;

  // Opened with a step waiting on the viewer further down: go to it. Once
  // per activity, so the page does not jump back there after every save --
  // and not when it is the first step, which is already in view below the
  // summary.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (detail === undefined || scrolledFor.current === detail._id) {
      return;
    }
    scrolledFor.current = detail._id;
    const index = detail.tasks.findIndex(stepWaitsOnViewer);
    if (index > 0) {
      document.getElementById(`task-${detail.tasks[index]!._id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detail]);




  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={detail?.titleSnapshot ?? "Activity detail"}
        description={detail?.descriptionSnapshot ?? ""}
        loading={detail === undefined}
        breadcrumbs={[
          { label: "CeMMbase", to: "/" },
          // The dashboard is the root, which the first crumb already is.
          ...(from === "dashboard" ? [] : [{ label: origin.label, to: origin.to }]),
          { label: detail?.titleSnapshot ?? "Activity detail" },
        ]}
        actions={
          <>
            {detail?.subject && !detail.subject.isViewer ? <AssigneeBlock subject={detail.subject} /> : null}
            <Button asChild variant="outline">
              <Link to={origin.to}>
                <LuArrowLeft />
                Back to {origin.label.toLowerCase()}
              </Link>
            </Button>
          </>
        }
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {detail === undefined ? (
          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body p={6}>
              <Text fontSize="sm" color="fg.muted">Loading activity detail…</Text>
            </Card.Body>
          </Card.Root>
        ) : (
          <>
            <ActivitySummary detail={detail} todayDay={todayDay} />

            <Stack as="section" gap={4}>
              <Heading size="xl">Steps</Heading>

              {/* A timeline, because the order is the point: each step
                  usually opens once the one before it is approved. */}
              <Timeline.Root size="xl" variant="solid">
                {detail.tasks.map((task) => (
                  <ActivityTaskCard
                    key={task._id}
                    task={task}
                    todayDay={todayDay}
                    // Whose record this is, for steps that belong to somebody
                    // other than them.
                    subjectName={detail.person
                      ? `${detail.person.firstName} ${detail.person.lastName}`
                      : null}
                    // The backend refuses a write to either, so the form must
                    // not invite one.
                    activityClosed={detail.status === "archived" || detail.status === "cancelled"}
                  />
                ))}
              </Timeline.Root>
            </Stack>

            <ActivityHistory history={detail.history} />
          </>
        )}
      </Stack>
    </Box>
  );
}
